import { useEffect } from "react";

import { getAbsoluteUrl, getSiteUrl, businessInfo } from "../lib/seo";

const upsertMeta = (selector, attributes) => {
  let element = document.head.querySelector(selector);

  if (!element) {
    element = document.createElement("meta");
    document.head.appendChild(element);
  }

  Object.entries(attributes).forEach(([key, value]) => {
    if (value) {
      element.setAttribute(key, value);
    }
  });
};

const upsertLink = (selector, attributes) => {
  let element = document.head.querySelector(selector);

  if (!element) {
    element = document.createElement("link");
    document.head.appendChild(element);
  }

  Object.entries(attributes).forEach(([key, value]) => {
    if (value) {
      element.setAttribute(key, value);
    }
  });
};

const upsertSchema = (schema) => {
  const existing = document.getElementById("seo-schema");

  if (!schema) {
    existing?.remove();
    return;
  }

  const script = existing || document.createElement("script");
  script.id = "seo-schema";
  script.type = "application/ld+json";
  script.textContent = JSON.stringify(schema);

  if (!existing) {
    document.head.appendChild(script);
  }
};

export const useSEO = ({
  title,
  description,
  keywords,
  canonicalPath,
  ogType = "website",
  ogImage = "/images/logo.jpeg",
  robots = "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1",
  schema,
}) => {
  useEffect(() => {
    if (typeof document === "undefined") {
      return undefined;
    }

    const canonicalUrl = canonicalPath ? getAbsoluteUrl(canonicalPath) : window.location.href;
    const imageUrl = ogImage.startsWith("http") ? ogImage : getAbsoluteUrl(ogImage);

    if (
      window.location.protocol === "http:" &&
      !["localhost", "127.0.0.1"].includes(window.location.hostname) &&
      getSiteUrl().startsWith("https://")
    ) {
      window.location.replace(window.location.href.replace("http://", "https://"));
      return undefined;
    }

    if (title) {
      document.title = title;
      upsertMeta('meta[property="og:title"]', { property: "og:title", content: title });
      upsertMeta('meta[name="twitter:title"]', { name: "twitter:title", content: title });
    }

    if (description) {
      upsertMeta('meta[name="description"]', { name: "description", content: description });
      upsertMeta('meta[property="og:description"]', {
        property: "og:description",
        content: description,
      });
      upsertMeta('meta[name="twitter:description"]', {
        name: "twitter:description",
        content: description,
      });
    }

    if (keywords) {
      upsertMeta('meta[name="keywords"]', { name: "keywords", content: keywords });
    }

    upsertMeta('meta[name="robots"]', { name: "robots", content: robots });
    upsertMeta('meta[name="googlebot"]', { name: "googlebot", content: robots });
    upsertMeta('meta[name="author"]', { name: "author", content: businessInfo.name });
    upsertMeta('meta[name="geo.region"]', { name: "geo.region", content: "IN-GJ" });
    upsertMeta('meta[name="geo.placename"]', { name: "geo.placename", content: "Vadodara, Gujarat" });
    upsertMeta('meta[name="geo.position"]', {
      name: "geo.position",
      content: `${businessInfo.geo.latitude};${businessInfo.geo.longitude}`,
    });
    upsertMeta('meta[name="ICBM"]', {
      name: "ICBM",
      content: `${businessInfo.geo.latitude}, ${businessInfo.geo.longitude}`,
    });
    upsertMeta('meta[property="og:type"]', { property: "og:type", content: ogType });
    upsertMeta('meta[property="og:url"]', { property: "og:url", content: canonicalUrl });
    upsertMeta('meta[property="og:site_name"]', { property: "og:site_name", content: businessInfo.name });
    upsertMeta('meta[property="og:locale"]', { property: "og:locale", content: "en_IN" });
    upsertMeta('meta[property="og:image"]', { property: "og:image", content: imageUrl });
    upsertMeta('meta[property="og:image:alt"]', {
      property: "og:image:alt",
      content: `${businessInfo.name} industrial pipeline services in Vadodara, Gujarat`,
    });
    upsertMeta('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
    upsertMeta('meta[name="twitter:url"]', { name: "twitter:url", content: canonicalUrl });
    upsertMeta('meta[name="twitter:image"]', { name: "twitter:image", content: imageUrl });
    upsertLink('link[rel="canonical"]', { rel: "canonical", href: canonicalUrl });
    upsertLink('link[rel="alternate"][hreflang="en-IN"]', {
      rel: "alternate",
      hreflang: "en-IN",
      href: canonicalUrl,
    });

    upsertSchema(schema);

    return undefined;
  }, [canonicalPath, description, keywords, ogImage, ogType, robots, schema, title]);
};
